// Applies the user's display settings to the document (spec §11.7 Görünüm / Erişilebilirlik): theme + motion through
// the prefs store (ThemeProvider), everything else as `data-*` attributes / CSS variables on <html> that
// styles/user-settings.css reads. A snapshot is kept in localStorage so a reload paints with the right zoom, density
// and saturation before READY arrives (the theme itself is persisted by store/prefs.ts).
import type { SettingsDTO } from '@moods/shared';
import { setUiHourCycle, type ThemePreference } from '@moods/ui';
import { usePrefs } from '../../store/prefs';

const KEY = 'moods.appearance';

/** The subset of settings that changes how the app is drawn. */
export interface AppearanceSnapshot {
  accent: string | null;
  density: 'cozy' | 'compact';
  fontScale: number;
  zoom: number;
  saturation: number;
  sidebarDensity: 'cozy' | 'compact';
  reducedTransparency: boolean;
  showAvatarsInCompact: boolean;
  focusIndicators: 'auto' | 'always';
  presenceLabels: boolean;
  animationLevel: 'full' | 'reduced' | 'none';
}

export const DEFAULT_APPEARANCE: AppearanceSnapshot = {
  accent: null,
  density: 'cozy',
  fontScale: 15,
  zoom: 100,
  saturation: 100,
  sidebarDensity: 'cozy',
  reducedTransparency: false,
  showAvatarsInCompact: true,
  focusIndicators: 'auto',
  presenceLabels: false,
  animationLevel: 'full',
};

export function snapshotOf(s: SettingsDTO): AppearanceSnapshot {
  return {
    accent: s.accent,
    density: s.density,
    fontScale: s.fontScale,
    zoom: s.zoom,
    saturation: s.saturation,
    sidebarDensity: s.appearance.sidebarDensity,
    reducedTransparency: s.appearance.reducedTransparency,
    showAvatarsInCompact: s.appearance.showAvatarsInCompact,
    focusIndicators: s.accessibility.focusIndicators,
    presenceLabels: s.accessibility.presenceLabels,
    animationLevel: s.accessibility.animationLevel,
  };
}

/** Theme actually painted: the high-contrast switch (Erişilebilirlik) wins over the Görünüm choice. */
export const effectiveTheme = (s: Pick<SettingsDTO, 'theme' | 'highContrast'>): ThemePreference => (s.highContrast ? 'high-contrast' : s.theme);

/**
 * Reduced motion: an explicit "reduce"/"full" wins; "system" follows the OS unless the animation level asks for
 * fewer animations ("reduced" / "none" force the reduced variants everywhere).
 */
export function effectiveMotion(s: Pick<SettingsDTO, 'reducedMotion'> & { accessibility: Pick<SettingsDTO['accessibility'], 'animationLevel'> }): 'system' | 'reduce' | 'full' {
  if (s.reducedMotion !== 'system') return s.reducedMotion;
  return s.accessibility.animationLevel === 'full' ? 'system' : 'reduce';
}

const isHex = (v: string | null): v is string => Boolean(v && /^#[0-9a-fA-F]{6}$/.test(v));

/** Writes the snapshot onto <html>. Pure DOM work; safe to call repeatedly. */
export function applyAppearance(a: AppearanceSnapshot, root: HTMLElement | null = typeof document !== 'undefined' ? document.documentElement : null): void {
  if (!root) return;
  const set = (name: string, value: string | null) => {
    if (value === null) root.removeAttribute(name);
    else root.setAttribute(name, value);
  };
  set('data-density', a.density === 'compact' ? 'compact' : null);
  set('data-sidebar-density', a.sidebarDensity === 'compact' ? 'compact' : null);
  set('data-reduced-transparency', a.reducedTransparency ? 'on' : null);
  set('data-focus', a.focusIndicators === 'always' ? 'always' : null);
  set('data-presence-labels', a.presenceLabels ? 'on' : null);
  set('data-animation', a.animationLevel === 'full' ? null : a.animationLevel);
  set('data-compact-avatars', a.density === 'compact' && !a.showAvatarsInCompact ? 'off' : null);
  const zoom = Math.min(150, Math.max(80, Math.round(a.zoom)));
  root.style.setProperty('--app-zoom', String(zoom / 100));
  const font = Math.min(20, Math.max(12, Math.round(a.fontScale)));
  if (font === DEFAULT_APPEARANCE.fontScale) root.style.removeProperty('--chat-font-size');
  else root.style.setProperty('--chat-font-size', `${font}px`);
  const sat = Math.min(100, Math.max(0, Math.round(a.saturation)));
  set('data-saturation', sat < 100 ? 'reduced' : null);
  root.style.setProperty('--app-saturation', String(sat / 100));
  if (isHex(a.accent)) root.style.setProperty('--user-accent', a.accent);
  else root.style.removeProperty('--user-accent');
  set('data-user-accent', isHex(a.accent) ? 'on' : null);
}

function persist(a: AppearanceSnapshot) {
  try {
    localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    // storage unavailable (private mode)
  }
}

/** Last applied snapshot (localStorage), or the defaults. */
export function restoredAppearance(): AppearanceSnapshot {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULT_APPEARANCE, ...(JSON.parse(raw) as Partial<AppearanceSnapshot>) };
  } catch {
    // ignore
  }
  return DEFAULT_APPEARANCE;
}

/**
 * Everything a settings change means for this client: theme / motion (prefs store → ThemeProvider), the appearance
 * attributes, and the local snapshot. Called for READY, USER_SETTINGS_UPDATE and optimistic saves.
 */
export function applyClientSettings(s: SettingsDTO): void {
  const prefs = usePrefs.getState();
  const theme = effectiveTheme(s);
  if (prefs.theme !== theme) prefs.setTheme(theme);
  const motion = effectiveMotion(s);
  if (prefs.motion !== motion) prefs.setMotion(motion);
  // 12/24-hour clock for every chat timestamp drawn by @moods/ui (Dil › Saat biçimi)
  setUiHourCycle(s.hour24 ? 'h23' : 'h12');
  const snap = snapshotOf(s);
  applyAppearance(snap);
  persist(snap);
}

/** Paint the stored snapshot as early as possible (imported by main.tsx before the first render). */
export function restoreAppearance(): void {
  applyAppearance(restoredAppearance());
}
